<template>
    <div>
        <rkm-modal
            persistent
            ref="RkmModal"
            title="Escolha de horario"
        >
            <rkm-form
                v-if="model"
                ref="RkmForm"
                show-legend
            >
                <rkm-row v-if="data.form.horariosComSituacao && data.form.horariosComSituacao.length > 0">
                    <rkm-col>
                        <rkm-alert color="warning">
                            <ul class="pl-3 mb-0">
                                <li
                                    v-for="horarioComSituacao in data.form.horariosComSituacao"
                                    :key="horarioComSituacao.inicio"
                                >
                                    Das {{horarioComSituacao.inicio}} às {{horarioComSituacao.fim}}: {{horarioComSituacao.situacaoNome}}
                                </li>
                            </ul>
                        </rkm-alert>
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-input
                            :value="data.form.tipoNome"
                            disabled
                            label="Tipo de agendamento"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-radio-group
                            :options="horarios"
                            :validations="validations.horario"
                            @input="handleHorarioInput"
                            label="Horario"
                            v-model="model.horario"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-textarea
                            :validations="validations.observacao"
                            label="Observação"
                            rows="4"
                            v-model="model.observacao"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row v-if="horarioMaximoAtingido">
                    <rkm-col>
                        <rkm-form-textarea
                            :validations="validations.motivoAgendamentoExcedente"
                            label="Motivo"
                            rows="4"
                            v-model="model.motivoAgendamentoExcedente"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-form>

            <template v-slot:footer="{cancel}">
                <rkm-button @click="cancel">
                    Fechar
                </rkm-button>

                <rkm-button
                    v-if="isButtonVisible"
                    @click="handleSave"
                    :authorize="`${id ? 'update' : 'store'}-ambulatorio-agendamento`"
                    color="primary"
                >
                    Salvar
                </rkm-button>
            </template>
        </rkm-modal>

        <ModalSupervisor
            @authorized="authorized"
            @canceled="canceled"
            ref="ModalSupervisor"
        />
    </div>
</template>

<script>
// Components.
import ModalSupervisor from '@/components/ambulatorio/components/supervisor/Modal'

// Mixins.
import {error, file, object, repository, stringCase} from '@/mixins'

// Vuex.
import {mapActions} from 'vuex'

export default {
    components: {ModalSupervisor},
    computed: {
        horarios() {
            return this.data.form.horarios && this.data.form.horarios.map(horario => {
                return {
                    value: horario.horario,
                    text: `${horario.horario} - ${horario.quantidadeAgendada}/${horario.quantidadeTotal}`
                }
            })
        },
        validations() {
            return {
                horario: {required: true},
                observacao: {max: 500},
                motivoAgendamentoExcedente: {
                    required: true,
                    max: 500,
                },
            }
        },
    },
    mounted() {
        this.onScreenOpen(); // Chamado quando o componente é montado
    },
    created() {
        this.repositories = {
            agendamento: this.getConectaSusRepository('ambulatorio.agendamento'),
            impressao: this.getConectaSusRepository('impressao.ambulatorio'),
        }
    },
    data: () => ({
        horarioMaximoAtingido: false,
        id: null,
        model: null,
        isButtonVisible: true,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        open(infraestruturaId, ocupacaoId, especialidadeId, usuarioId, data, agendamentoId = null) {
            this.isButtonVisible = true;
            
            this.resetForm()

            this.$refs.RkmModal.show()

            this.id = agendamentoId

            const request = {
                origem: 'cadastro',
                listar: 'horarios',
                pacienteId: this.pacienteId,
                infraestruturaId,
                ocupacaoId,
                especialidadeId,
                usuarioId,
                data,
                agendamentoId,
            }

            this.setLoading()

            this.repositories.agendamento.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(({data}) => {
                    this.data.form.tipoNome = data.tipoNome
                    this.data.form.horarios = Object.assign(data.horarios)
                    this.data.form.horariosComSituacao = Object.assign(data.horariosComSituacao)

                    this.model = this.clone(this.data.model)

                    delete this.model.horario
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleHorarioInput(value) {
            this.horarioMaximoAtingido = false
            this.model.motivoAgendamentoExcedente = null

            const horario = this.data.form.horarios.find(horario => horario.horario === value)

            const horarioMaximoAtingido = this.data.form.horarios
                .filter(horario => horario.quantidadeAgendada >= horario.quantidadeTotal)
                .find(horario => horario.horario == value)

            if (horarioMaximoAtingido || (horario && horario.obrigatorioAutenticacaoSupervisor)) {
                this.$refs.ModalSupervisor.open(!!horarioMaximoAtingido)
            }
        },
        async store() {
            const horario = this.data.form.horarios.find(horario => horario.horario === this.model.horario)

            let request = {
                pacienteId: this.pacienteId,
                jornadaId: horario.jornadaId,
                horario: horario.horario,
                observacao: this.model.observacao,
                regulacaoSolicitacaoId: this.regulacaoSolicitacao?.id ?? null,
            }

            if (this.horarioMaximoAtingido) {
                request.motivoAgendamentoExcedente = this.model.motivoAgendamentoExcedente
            }

            this.setLoading()

            this.repositories.agendamento.store(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.print(response.data.id)
                    this.$refs.RkmModal.hide()
                    this.$emit('saved', response.data)
                    this.showSaveNotification()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        async update() {
            const horario = this.data.form.horarios.find(horario => horario.horario === this.model.horario)

            const request = {
                jornadaId: horario.jornadaId,
                horario: horario.horario,
                observacao: this.model.observacao,
            }

            if (this.isSupervisor) {
                request.motivoAgendamentoExcedente = this.model.motivoAgendamentoExcedente
            }

            this.setLoading()

            this.repositories.agendamento.update(this.id, this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.print(response.data.id)
                    this.$refs.RkmModal.hide()
                    this.$emit('saved', response.data)
                    this.showSaveNotification()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleSave() {            

            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    this.isButtonVisible = false;
                    
                    if (this.data.form.faltas.length > 0) {
                        let message = ''

                        this.data.form.faltas.forEach(falta => {
                            message = message + `<li>${falta.data} às ${falta.horario}, ${falta.especialidade}, ${falta.usuario}</li>`
                        })

                        this.showConfirmation(
                            `
                                O paciente possui ${this.data.form.faltas.length} falta(s) na(s) seguinte(s) data(s) e especialidade(s):
                                <br/><br/>
                                <ul>${message}</ul>
                                Deseja continuar?
                            `,
                            async () => {
                                await this.save()
                            },
                            () => {
                                this.hide()
                            }
                        )
                    } else {
                        this.save()
                    }
                })
                .catch(() => {

                    this.isButtonVisible = true;
                });
        },
        async save() {
            if (this.id) {
                await this.update();
            } else {
                await this.store();
            }
        },
        onScreenOpen() {
            this.isButtonVisible = true;
        },
        print(id) {
            this.setLoading()

            this.repositories.impressao.agendamento(id)
                .then(({data, headers}) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        canceled() {
            this.model.horario = null

            this.$nextTick().then(() => this.$refs.RkmForm.resetValidator())
        },
        hide() {
            this.$refs.RkmModal.hide()
            this.$emit('hided')
        },
        resetForm() {
            this.data.form.horarios = null
            this.data.form.horariosComSituacao = null
            this.data.form.tipoNome = null
            this.horarioMaximoAtingido = false
            this.model = null
        },
        authorized(value) {
            this.horarioMaximoAtingido = value
            this.isSupervisor = value 
        },
    },
    mixins: [error, file, object, repository, stringCase],
    props: {
        data: {type: Object, required: true},
        pacienteId: {type: [Number, String], required: true},
        regulacaoSolicitacao: {type: Object},
    },
}
</script>